import { FC, useMemo } from 'react';
import { ParticipantDto, PeriodScore, PeriodScoreTags } from '../../api/sbapi/sbapi-api-model';
import {
	OngoingScoreLabel,
	ParticipantAndServeIndicator,
	ParticipantLabel,
	ScoreLabel,
	ScoreRow,
	ServeIndicator
} from './sportsbook-event-scoreboard-layout';

type Props = {
	row: number;
	participant: ParticipantDto;
	score?: number | null;
	ongoingPeriod?: PeriodScore;
	finishedPeriods?: PeriodScore[];
	size?: 'small' | 'large';
	isParticipantNameHighlighted?: boolean;
	isSecondaryScoreHighlighted?: boolean;
};

/**
 * Renders a single line of the score grid. Passing score overrides the ongoing period score, but rest from period will be shown (secondaryScore, serving indicator).
 * @param row - The row number of the score grid.
 * @param participant - The participant to display.
 * @param score - The score to display. Usually is a total score.
 * @param ongoingPeriod - The ongoing period to display. If score is not provided, primaryScore from this period will be shown instead.
 * @param finishedPeriods - The finished periods to display.
 * @param size - The size of the labels. Small is used on game-cards and large is used on event pages.
 * @param isParticipantNameHighlighted - Whether the participant name should be highlighted.
 * @param isSecondaryScoreHighlighted - Whether the secondary score should be highlighted.
 */
const ScoreLine: FC<Props> = ({
	row,
	participant,
	score,
	ongoingPeriod,
	finishedPeriods,
	size,
	isParticipantNameHighlighted,
	isSecondaryScoreHighlighted
}) => {
	const ongoingScore = useMemo(() => {
		// Important to use != instead of !== to catch both null and undefined.
		// 0 is a valid score.
		if (score != null) {
			return score;
		}
		// If score is not provided, show primary score from ongoing period.
		if (ongoingPeriod) {
			return ongoingPeriod.primaryScore;
		}
		return undefined;
	}, [score, ongoingPeriod]);

	return (
		<ScoreRow row={row}>
			{finishedPeriods &&
				finishedPeriods.map((period, index) => (
					<ScoreLabel
						// eslint-disable-next-line react/no-array-index-key
						key={`score-home-period-${index}`}
						isHighlighted={period.tags.includes(PeriodScoreTags.PERIOD_WINNER)}
						size={size}
					>
						{period.primaryScore}
					</ScoreLabel>
				))}
			{ongoingScore != null && <OngoingScoreLabel size={size}>{ongoingScore}</OngoingScoreLabel>}
			{ongoingPeriod?.secondaryScore != null && (
				<ScoreLabel isHighlighted={isSecondaryScoreHighlighted} size={size}>
					{ongoingPeriod.secondaryScore}
				</ScoreLabel>
			)}
			<ParticipantAndServeIndicator>
				<ParticipantLabel isHighlighted={isParticipantNameHighlighted} size={size}>
					{participant.name}
				</ParticipantLabel>
				{ongoingPeriod?.tags.includes(PeriodScoreTags.SERVING) && <ServeIndicator />}
			</ParticipantAndServeIndicator>
		</ScoreRow>
	);
};

export default ScoreLine;
